import { TextField, type TextFieldProps, InputAdornment } from '@mui/material';
import { forwardRef, type ReactNode } from 'react';
import { colors, radius, spacing, fonts } from '@/theme/tokens';

interface AppTextFieldProps extends Omit<TextFieldProps, 'variant'> {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
}

export const AppTextField = forwardRef<HTMLInputElement, AppTextFieldProps>(
  ({ startIcon, endIcon, sx, InputProps, ...props }, ref) => {
    return (
      <TextField
        inputRef={ref}
        variant="outlined"
        fullWidth
        InputProps={{
          startAdornment: startIcon ? (
            <InputAdornment position="start" sx={{ color: colors.textMuted }}>
              {startIcon}
            </InputAdornment>
          ) : undefined,
          endAdornment: endIcon ? (
            <InputAdornment position="end" sx={{ color: colors.textMuted }}>
              {endIcon}
            </InputAdornment>
          ) : undefined,
          ...InputProps,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: radius.md,
            backgroundColor: colors.surface,
            fontFamily: fonts.body,
            fontSize: '14px',
            '& fieldset': {
              borderColor: colors.border,
              borderWidth: '1px',
            },
            '&:hover fieldset': {
              borderColor: colors.textMuted,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.accent,
              borderWidth: '1px',
            },
            '&.Mui-error fieldset': {
              borderColor: colors.danger,
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.textMuted,
            fontFamily: fonts.body,
            fontSize: '14px',
            '&.Mui-focused': {
              color: colors.accent,
            },
            '&.Mui-error': {
              color: colors.danger,
            },
          },
          '& .MuiInputBase-input': {
            color: colors.textPrimary,
            padding: `${spacing[3]} ${spacing[4]}`,
            '&::placeholder': {
              color: colors.textMuted,
              opacity: 1,
            },
          },
          '& .MuiFormHelperText-root': {
            fontFamily: fonts.body,
            fontSize: '12px',
            fontWeight: 400,
            lineHeight: '16px',
            textTransform: 'none',
            letterSpacing: 'normal',
            color: colors.textSecondary,
            marginTop: spacing[1],
            marginLeft: 0,
            '&.Mui-error': {
              color: colors.danger,
            },
          },
          ...sx,
        }}
        {...props}
      />
    );
  }
);

AppTextField.displayName = 'AppTextField';
